import React, { useEffect } from "react";

import {
  faExclamationTriangle,
  faInfoCircle,
} from "@fortawesome/pro-regular-svg-icons";
import axios from "axios";
import { useController, useForm } from "react-hook-form";
import slugify from "slugify";

import { useAllAWSAccounts } from "src/adminPanel/queries";
import { WorkspaceWithSettings } from "src/api/types";
import { Input } from "src/base-components/Input";
import { Label } from "src/base-components/Label";
import { Select } from "src/base-components/Select";
import { WorkspaceSettings } from "src/clients/flow-api";
import {
  AWS_REGIONS,
  AwsRegionType,
  DISCOURAGED_AWS_REGIONS,
} from "src/constants/AwsRegions";
import { DOCS_RETENTION_POLICIES } from "src/constants/ExternalLinks";
import { Button } from "src/design-system/Button";
import { Callout } from "src/design-system/Callout";
import { ErrorHint } from "src/design-system/ErrorHint";
import { Icon } from "src/design-system/Icon";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { useIsFeatureFlagEnabled } from "src/hooks/useIsFeatureFlagEnabled";
import { FORM_DEFAULT_VALUES } from "src/layout/WorkspaceFormModal/constants";
import { DataRetentionUnitDropdown } from "src/layout/WorkspaceFormModal/DataRetentionUnitDropdown";
import { RegionComboBox } from "src/layout/WorkspaceFormModal/RegionComboBox";
import {
  getNameErrorMessage,
  nameValidations,
} from "src/layout/WorkspaceFormModal/utils";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { formatDate } from "src/utils/datetime";
import {
  getStringValidations,
  getWorkspaceSlugErrorMessage,
  WORKSPACE_SLUG_CONSTRAINTS,
} from "src/utils/validations";

type WorkspaceSettingsModalProps = {
  variant: "Create" | "Edit";
  isOpen: boolean;
  workspace?: WorkspaceWithSettings;
  wsId?: string;
  onClose: () => void;
  onSubmit: (data: CreateWorkspaceForm) => void;
};
const PROFILE_LEVELS = ["-1", "0", "1", "2", "3"];
export type CreateWorkspaceForm = {
  name: string;
  slug: string;
  region: AwsRegionType;
  profileLevel: string;
  aws_account_id?: string;
  flow_services_version?: string;
  settings?: WorkspaceSettings;
};

export const WorkspaceSettingsModal: React.FC<WorkspaceSettingsModalProps> = ({
  variant,
  isOpen,
  workspace: currentWorkspace,
  onClose,
  onSubmit,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    setError,
    reset,
    control,
    formState: { errors, isValid, isSubmitting },
  } = useForm<CreateWorkspaceForm>({
    defaultValues: FORM_DEFAULT_VALUES,
    shouldFocusError: true,
    mode: "onChange",
  });

  const { data: awsAccounts } = useAllAWSAccounts();
  const powertoolsEnabled = useIsFeatureFlagEnabled(FEATURE_FLAGS.powertools);

  // useController instead of <Controller>: render-prop Controllers can stop
  // delivering field updates after reset() once this component is compiled
  // (react-hook-form#12157).
  const { field: regionField } = useController({ control, name: "region" });
  const { field: retentionUnitField } = useController({
    control,
    name: "settings.data_retention.unit",
  });
  const { field: dataWipingField } = useController({
    control,
    name: "settings.data_wiping_enabled_until",
  });
  const { field: awsAccountField } = useController({
    control,
    name: "aws_account_id",
  });
  const { field: profileLevelField } = useController({
    control,
    name: "profileLevel",
  });
  const discouragedRegion =
    DISCOURAGED_AWS_REGIONS[regionField.value.identifier];
  useEffect(() => {
    const currentWorkspaceRegion = AWS_REGIONS.find(
      (region) => region.identifier === currentWorkspace?.aws_region,
    );
    const currentWorkspaceName = currentWorkspace?.name;

    if (currentWorkspaceRegion && currentWorkspaceName) {
      reset(
        {
          name: variant === "Edit" ? currentWorkspaceName : "",
          slug: "",
          region: currentWorkspaceRegion,
          aws_account_id: currentWorkspace?.aws_account_id,
          profileLevel: "0",
          settings: {
            data_retention: currentWorkspace?.settings?.data_retention,
            data_wiping_enabled_until:
              currentWorkspace?.settings?.data_wiping_enabled_until,
          },
          ...(isFeatureFlagEnabled(FEATURE_FLAGS.powertools) && {
            flow_services_version: currentWorkspace?.flow_services_version,
          }),
        },
        { keepFieldsRef: true },
      );
    }
  }, [currentWorkspace, reset, variant]);

  const onFormConfirm = handleSubmit(async (data: CreateWorkspaceForm) => {
    try {
      await onSubmit(data);
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 422) {
        setError("slug", { type: "notAvailable" }, { shouldFocus: true });
      } else {
        toastActions.failure({
          title: "Error submitting workspace configuration",
          description: "Please try again later.",
        });
      }
    }
  });

  return (
    <Modal
      afterLeave={() => reset(undefined, { keepFieldsRef: true })}
      open={isOpen}
      onClose={onClose}
    >
      <Modal.Header>{variant} workspace</Modal.Header>
      <form onSubmit={onFormConfirm}>
        <Modal.Content>
          <div className="flex flex-col gap-6">
            <div>
              <Label required>Workspace name</Label>
              <Input
                fullWidth
                {...register("name", {
                  required: true,
                  validate: nameValidations,
                  onChange: (e) => {
                    if (variant === "Create") {
                      setValue(
                        "slug",
                        slugify(e.target.value, { strict: true, lower: true }),
                        {
                          shouldValidate: true,
                        },
                      );
                    }
                  },
                })}
              />
              {errors.name && (
                <ErrorHint>{getNameErrorMessage(errors.name.type)}</ErrorHint>
              )}
            </div>
            {variant === "Create" && (
              <>
                <div>
                  <Label required>Slug</Label>
                  <Input
                    fullWidth
                    {...register("slug", {
                      required: true,
                      validate: getStringValidations(
                        WORKSPACE_SLUG_CONSTRAINTS,
                      ),
                    })}
                  />
                  {errors.slug && (
                    <ErrorHint>
                      {getWorkspaceSlugErrorMessage(errors.slug.type)}
                    </ErrorHint>
                  )}
                </div>
                <div>
                  <Label>Profile Level</Label>
                  <Select
                    value={profileLevelField.value}
                    options={PROFILE_LEVELS.map((pl) => ({
                      key: pl,
                      value: pl,
                    }))}
                    onChange={profileLevelField.onChange}
                  />
                </div>
              </>
            )}
            <div className="flex flex-col gap-y-2">
              <div>
                <Label required>Region</Label>
                <RegionComboBox
                  disabled={variant === "Edit"}
                  value={regionField.value}
                  onChange={regionField.onChange}
                />
              </div>
              {variant === "Create" && discouragedRegion && (
                <Callout dataLoc="discouraged-region-warning" variant="warning">
                  {regionField.value.identifier} {discouragedRegion.reason}.
                  Prefer{" "}
                  {new Intl.ListFormat("en", { type: "disjunction" }).format(
                    discouragedRegion.alternatives,
                  )}{" "}
                  unless this one is required.
                </Callout>
              )}
            </div>
            <div>
              <Label mb="mb-1" required>
                Retention policy
              </Label>
              <Tooltip
                body="Setting a data retention period will automatically delete Decision Flow history after the specified time period. This might impact analytics, backtesting, and other features."
                footerAction={{
                  text: "Read more",
                  onClick: () => window.open(DOCS_RETENTION_POLICIES, "_blank"),
                }}
                placement="top"
                title="Retention policy"
              >
                <Icon icon={faInfoCircle} size="2xs" />
              </Tooltip>
              <div className="mb-2 font-inter-normal-12px text-gray-500">
                Automatically delete decision history older than
              </div>

              <div>
                <div className="flex items-center gap-x-2">
                  <Input
                    type="number"
                    fullWidth
                    {...register("settings.data_retention.value", {
                      required: "This field cannot be empty",
                      min: {
                        value: 0,
                        message: "The value must be a positive integer",
                      },
                      valueAsNumber: true,
                    })}
                    errored={!!errors.settings?.data_retention?.value}
                  />
                  <div className="w-32 shrink-0">
                    <DataRetentionUnitDropdown
                      value={retentionUnitField.value ?? "days"}
                      onChange={retentionUnitField.onChange}
                    />
                  </div>
                </div>

                {errors.settings?.data_retention?.value && (
                  <ErrorHint>
                    {errors.settings?.data_retention?.value?.message}
                  </ErrorHint>
                )}
                {variant === "Edit" && (
                  <div className="mt-4">
                    <Label mb="mb-1">Data wiping</Label>
                    {!dataWipingField.value ||
                    new Date(dataWipingField.value) < new Date() ? (
                      <div className="flex items-center gap-x-2">
                        <div className="font-inter-normal-12px text-gray-500">
                          Data wiping is disabled
                        </div>
                        <Button
                          iconLeft={faExclamationTriangle}
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            dataWipingField.onChange(
                              new Date(
                                Date.now() + 4 * 60 * 60 * 1000,
                              ).toISOString(),
                            );
                          }}
                        >
                          Enable for 4 hours
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-x-2">
                        <div className="font-inter-normal-12px text-gray-500">
                          Data wiping enabled until{" "}
                          {formatDate(dataWipingField.value)}
                        </div>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            dataWipingField.onChange(undefined);
                          }}
                        >
                          Disable
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            {powertoolsEnabled && (
              <div className="mt-8 text-gray-500">
                <div className="mb-1 font-inter-semibold-13px text-gray-800">
                  [⚡ POWERTOOL] Workspace services version
                </div>
                <Input
                  fullWidth
                  {...register("flow_services_version", {
                    required: false,
                    validate: (value) =>
                      value?.length === 0 || value?.includes("/"),
                  })}
                />
              </div>
            )}
            {powertoolsEnabled && (
              <div>
                <Label mb="mb-1" required>
                  AWS Account
                </Label>
                <div>
                  <Select
                    disabled={variant === "Edit"}
                    options={
                      awsAccounts?.map((account) => ({
                        key: account.id,
                        value: (
                          <span>
                            {account.name}
                            <span className="ml-1.5 text-gray-400">
                              ({account.account_id})
                            </span>
                          </span>
                        ),
                      })) ?? []
                    }
                    value={awsAccountField.value}
                    onChange={awsAccountField.onChange}
                  />
                </div>
              </div>
            )}
          </div>
        </Modal.Content>
        <Modal.Footer
          primaryButton={
            <Button
              isDisabled={!isValid || isSubmitting}
              htmlType="submit"
              isLoading={isSubmitting}
              variant="primary"
            >
              Save
            </Button>
          }
        />
      </form>
    </Modal>
  );
};
